import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Stack,
  SimpleGrid,
  Card,
  Group,
  ThemeIcon,
  Badge,
  Button,
  Modal,
  TextInput,
  Select,
  Textarea,
  Switch,
  NumberInput,
  ActionIcon,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  IconBox,
  IconChecks,
  IconAlertTriangle,
  IconCalendar,
  IconCurrencyDollar,
  IconTrendingUp,
  IconChartBar,
  IconRefresh,
  IconMapPin,
  IconBuilding,
  IconPlayerPlay,
  IconClockPlay,
  IconShield,
  IconTool,
  IconUsers,
} from '@tabler/icons-react';
import { useOrganization } from '@/hooks/useOrganization';
import { useCreateCustomReport } from '@/hooks/api/useCustomReports';
import {
  useCreateReportSchedule,
  type CreateScheduleInput,
  type Recipient,
} from '@/hooks/api/useReportSchedules';

interface ReportPreset {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  badge?: string;
  config: {
    entities: string[];
    fields: Array<{ field: string; label: string; format?: string }>;
    filters?: Record<string, any>;
    groupBy?: Array<{ field: string }>;
    aggregations?: Array<{ type: string; field: string; alias: string }>;
    sortBy?: Array<{ field: string; direction: 'asc' | 'desc' }>;
  };
}

const REPORT_PRESETS: ReportPreset[] = [
  {
    id: 'all-assets',
    name: 'Complete Asset Inventory',
    description: 'Full list of all assets with key details',
    icon: <IconBox size={28} />,
    color: 'blue',
    badge: 'Popular',
    config: {
      entities: ['assets'],
      fields: [
        { field: 'tag_id', label: 'Tag ID' },
        { field: 'name', label: 'Name' },
        { field: 'brand', label: 'Brand' },
        { field: 'model', label: 'Model' },
        { field: 'status', label: 'Status' },
        { field: 'cost', label: 'Cost', format: 'currency' },
        { field: 'category_name', label: 'Category' },
        { field: 'location_name', label: 'Location' },
      ],
      sortBy: [{ field: 'tag_id', direction: 'asc' }],
    },
  },
  {
    id: 'assets-by-status',
    name: 'Assets by Status',
    description: 'Summary grouped by asset status with counts and values',
    icon: <IconChartBar size={28} />,
    color: 'indigo',
    badge: 'Summary',
    config: {
      entities: ['assets'],
      fields: [
        { field: 'status', label: 'Status' },
        { field: 'tag_id', label: 'Tag ID' },
        { field: 'name', label: 'Name' },
        { field: 'cost', label: 'Cost', format: 'currency' },
      ],
      groupBy: [{ field: 'status' }],
      aggregations: [
        { type: 'count', field: '*', alias: 'asset_count' },
        { type: 'sum', field: 'cost', alias: 'total_value' },
      ],
    },
  },
  {
    id: 'assets-by-category',
    name: 'Assets by Category',
    description: 'Assets grouped by category with totals',
    icon: <IconBuilding size={28} />,
    color: 'grape',
    badge: 'Summary',
    config: {
      entities: ['assets'],
      fields: [
        { field: 'category_name', label: 'Category' },
        { field: 'tag_id', label: 'Tag ID' },
        { field: 'name', label: 'Name' },
        { field: 'status', label: 'Status' },
        { field: 'cost', label: 'Cost', format: 'currency' },
      ],
      groupBy: [{ field: 'category_name' }],
      aggregations: [
        { type: 'count', field: '*', alias: 'asset_count' },
        { type: 'sum', field: 'cost', alias: 'total_value' },
      ],
    },
  },
  {
    id: 'assets-by-location',
    name: 'Assets by Location',
    description: 'Assets grouped by location',
    icon: <IconMapPin size={28} />,
    color: 'teal',
    config: {
      entities: ['assets'],
      fields: [
        { field: 'location_name', label: 'Location' },
        { field: 'tag_id', label: 'Tag ID' },
        { field: 'name', label: 'Name' },
        { field: 'category_name', label: 'Category' },
        { field: 'status', label: 'Status' },
      ],
      groupBy: [{ field: 'location_name' }],
      aggregations: [{ type: 'count', field: '*', alias: 'asset_count' }],
    },
  },
  {
    id: 'available-assets',
    name: 'Available Assets',
    description: 'Assets ready for assignment',
    icon: <IconChecks size={28} />,
    color: 'green',
    config: {
      entities: ['assets'],
      fields: [
        { field: 'tag_id', label: 'Tag ID' },
        { field: 'name', label: 'Name' },
        { field: 'brand', label: 'Brand' },
        { field: 'model', label: 'Model' },
        { field: 'category_name', label: 'Category' },
        { field: 'location_name', label: 'Location' },
      ],
      filters: { status: ['Available'] },
      sortBy: [{ field: 'category_name', direction: 'asc' }],
    },
  },
  {
    id: 'maintenance-assets',
    name: 'Assets in Maintenance',
    description: 'Assets currently under maintenance',
    icon: <IconTool size={28} />,
    color: 'orange',
    badge: 'Action Required',
    config: {
      entities: ['assets'],
      fields: [
        { field: 'tag_id', label: 'Tag ID' },
        { field: 'name', label: 'Name' },
        { field: 'brand', label: 'Brand' },
        { field: 'model', label: 'Model' },
        { field: 'location_name', label: 'Location' },
        { field: 'next_maintenance_date', label: 'Next Maintenance', format: 'date' },
      ],
      filters: { status: ['Maintenance'] },
    },
  },
  {
    id: 'high-value-assets',
    name: 'High Value Assets',
    description: 'Assets valued at $5,000 or more',
    icon: <IconCurrencyDollar size={28} />,
    color: 'yellow',
    config: {
      entities: ['assets'],
      fields: [
        { field: 'tag_id', label: 'Tag ID' },
        { field: 'name', label: 'Name' },
        { field: 'brand', label: 'Brand' },
        { field: 'model', label: 'Model' },
        { field: 'cost', label: 'Cost', format: 'currency' },
        { field: 'purchase_date', label: 'Purchase Date', format: 'date' },
        { field: 'location_name', label: 'Location' },
      ],
      filters: { costMin: 5000 },
      sortBy: [{ field: 'cost', direction: 'desc' }],
    },
  },
  {
    id: 'warranty-expiring',
    name: 'Warranty Expiring Soon',
    description: 'Assets with warranties expiring in 90 days',
    icon: <IconShield size={28} />,
    color: 'red',
    badge: 'Action Required',
    config: {
      entities: ['assets'],
      fields: [
        { field: 'tag_id', label: 'Tag ID' },
        { field: 'name', label: 'Name' },
        { field: 'brand', label: 'Brand' },
        { field: 'model', label: 'Model' },
        { field: 'warranty_expiration', label: 'Warranty Expires', format: 'date' },
        { field: 'cost', label: 'Cost', format: 'currency' },
      ],
      filters: { warrantyExpiringDays: 90 },
      sortBy: [{ field: 'warranty_expiration', direction: 'asc' }],
    },
  },
  {
    id: 'recent-purchases',
    name: 'Recent Purchases',
    description: 'Assets purchased in the last 30 days',
    icon: <IconCalendar size={28} />,
    color: 'violet',
    config: {
      entities: ['assets'],
      fields: [
        { field: 'tag_id', label: 'Tag ID' },
        { field: 'name', label: 'Name' },
        { field: 'brand', label: 'Brand' },
        { field: 'model', label: 'Model' },
        { field: 'cost', label: 'Cost', format: 'currency' },
        { field: 'purchase_date', label: 'Purchase Date', format: 'date' },
        { field: 'purchased_from', label: 'Vendor' },
      ],
      filters: { purchasedLastDays: 30 },
      sortBy: [{ field: 'purchase_date', direction: 'desc' }],
    },
  },
  {
    id: 'depreciation-report',
    name: 'Depreciation Report',
    description: 'Depreciable assets with current values',
    icon: <IconTrendingUp size={28} />,
    color: 'cyan',
    config: {
      entities: ['assets'],
      fields: [
        { field: 'tag_id', label: 'Tag ID' },
        { field: 'name', label: 'Name' },
        { field: 'cost', label: 'Original Cost', format: 'currency' },
        { field: 'purchase_date', label: 'Purchase Date', format: 'date' },
        { field: 'depreciation_method', label: 'Method' },
        { field: 'useful_life_months', label: 'Useful Life (months)' },
        { field: 'salvage_value', label: 'Salvage Value', format: 'currency' },
      ],
      filters: { isDepreciable: true },
      sortBy: [{ field: 'purchase_date', direction: 'asc' }],
    },
  },
  {
    id: 'assigned-assets',
    name: 'Asset Assignments',
    description: 'All assets with their current assignments',
    icon: <IconUsers size={28} />,
    color: 'pink',
    config: {
      entities: ['assets'],
      fields: [
        { field: 'tag_id', label: 'Tag ID' },
        { field: 'name', label: 'Name' },
        { field: 'status', label: 'Status' },
        { field: 'assigned_to', label: 'Assigned To' },
        { field: 'department_name', label: 'Department' },
        { field: 'location_name', label: 'Location' },
      ],
      filters: { status: ['Assigned', 'In Use'] },
      sortBy: [{ field: 'assigned_to', direction: 'asc' }],
    },
  },
  {
    id: 'recently-updated',
    name: 'Recently Updated',
    description: 'Assets modified in the last 7 days',
    icon: <IconRefresh size={28} />,
    color: 'lime',
    config: {
      entities: ['assets'],
      fields: [
        { field: 'tag_id', label: 'Tag ID' },
        { field: 'name', label: 'Name' },
        { field: 'status', label: 'Status' },
        { field: 'updated_at', label: 'Last Updated', format: 'datetime' },
        { field: 'category_name', label: 'Category' },
      ],
      filters: { updatedLastDays: 7 },
      sortBy: [{ field: 'updated_at', direction: 'desc' }],
    },
  },
];

const DAYS_OF_WEEK = [
  { value: '0', label: 'Sunday' },
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
];

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'UTC', label: 'UTC' },
];

export function ReportPresetsPage() {
  const navigate = useNavigate();
  const { organizationId } = useOrganization();
  const createReport = useCreateCustomReport();
  const createSchedule = useCreateReportSchedule();

  const [selectedPreset, setSelectedPreset] = useState<ReportPreset | null>(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [recipientInput, setRecipientInput] = useState('');

  const form = useForm({
    initialValues: {
      name: '',
      frequency: 'weekly' as 'daily' | 'weekly' | 'monthly',
      day_of_week: '1',
      day_of_month: 1,
      time_of_day: '08:00',
      timezone: 'America/New_York',
      recipients: [] as string[],
      email_subject: '',
      email_intro: '',
      include_csv: true,
      include_summary: true,
    },
    validate: {
      name: (value) => (!value ? 'Name is required' : null),
      recipients: (value) => (value.length === 0 ? 'Add at least one recipient' : null),
    },
  });

  const handleRunPreset = async (preset: ReportPreset) => {
    if (!organizationId) return;

    try {
      // Create a custom report from the preset
      const report = await createReport.mutateAsync({
        organization_id: organizationId,
        name: preset.name,
        description: preset.description,
        config: preset.config,
        is_shared: false,
      });

      notifications.show({
        title: 'Report Created',
        message: `"${preset.name}" has been saved to your reports`,
        color: 'green',
      });

      // Navigate to saved reports
      navigate('/reports/saved');
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to create report',
        color: 'red',
      });
    }
  };

  const handleSchedulePreset = (preset: ReportPreset) => {
    setSelectedPreset(preset);
    form.setValues({
      ...form.values,
      name: `${preset.name} - Weekly`,
    });
    setScheduleModalOpen(true);
  };

  const handleSubmitSchedule = async (values: typeof form.values) => {
    if (!organizationId || !selectedPreset) return;

    try {
      // First create the custom report
      const report = await createReport.mutateAsync({
        organization_id: organizationId,
        name: selectedPreset.name,
        description: selectedPreset.description,
        config: selectedPreset.config,
        is_shared: false,
      });

      // Then create the schedule
      const recipients: Recipient[] = values.recipients.map((email) => ({ email }));

      const input: CreateScheduleInput = {
        report_id: report.id,
        organization_id: organizationId,
        name: values.name,
        frequency: values.frequency,
        day_of_week: values.frequency === 'weekly' ? parseInt(values.day_of_week) : undefined,
        day_of_month: values.frequency === 'monthly' ? values.day_of_month : undefined,
        time_of_day: values.time_of_day + ':00',
        timezone: values.timezone,
        recipients,
        email_subject: values.email_subject || undefined,
        email_intro: values.email_intro || undefined,
        include_csv: values.include_csv,
        include_summary: values.include_summary,
      };

      await createSchedule.mutateAsync(input);

      notifications.show({
        title: 'Schedule Created',
        message: `"${values.name}" has been scheduled`,
        color: 'green',
      });

      setScheduleModalOpen(false);
      form.reset();
      navigate('/reports/automations');
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to create schedule',
        color: 'red',
      });
    }
  };

  const addRecipient = () => {
    const email = recipientInput.trim();
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      if (!form.values.recipients.includes(email)) {
        form.setFieldValue('recipients', [...form.values.recipients, email]);
      }
      setRecipientInput('');
    }
  };

  return (
    <Container fluid py="xl">
      <Stack gap="xl">
        <div>
          <Title order={1}>Report Presets</Title>
          <Text size="sm" c="dimmed" mt={4}>
            Quick-start templates for common reports. Run instantly or schedule for automatic delivery.
          </Text>
        </div>

        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
          {REPORT_PRESETS.map((preset) => (
            <Card
              key={preset.id}
              shadow="sm"
              padding="lg"
              radius="md"
              withBorder
            >
              <Stack gap="md">
                <Group justify="space-between">
                  <ThemeIcon size={50} radius="md" variant="light" color={preset.color}>
                    {preset.icon}
                  </ThemeIcon>
                  {preset.badge && (
                    <Badge size="sm" variant="dot" color={preset.color}>
                      {preset.badge}
                    </Badge>
                  )}
                </Group>

                <div>
                  <Text fw={600} size="lg" mb={4}>
                    {preset.name}
                  </Text>
                  <Text size="sm" c="dimmed" lineClamp={2}>
                    {preset.description}
                  </Text>
                </div>

                <Group gap="xs" mt="auto">
                  <Button
                    variant="light"
                    size="sm"
                    leftSection={<IconPlayerPlay size={16} />}
                    onClick={() => handleRunPreset(preset)}
                    loading={createReport.isPending}
                    style={{ flex: 1 }}
                  >
                    Save & Run
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    leftSection={<IconClockPlay size={16} />}
                    onClick={() => handleSchedulePreset(preset)}
                  >
                    Schedule
                  </Button>
                </Group>
              </Stack>
            </Card>
          ))}
        </SimpleGrid>
      </Stack>

      {/* Schedule Modal */}
      <Modal
        opened={scheduleModalOpen}
        onClose={() => setScheduleModalOpen(false)}
        title={`Schedule: ${selectedPreset?.name}`}
        size="lg"
      >
        <form onSubmit={form.onSubmit(handleSubmitSchedule)}>
          <Stack gap="md">
            <TextInput
              label="Schedule Name"
              placeholder="e.g., Weekly Asset Summary"
              {...form.getInputProps('name')}
              required
            />

            <Select
              label="Frequency"
              data={[
                { value: 'daily', label: 'Daily' },
                { value: 'weekly', label: 'Weekly' },
                { value: 'monthly', label: 'Monthly' },
              ]}
              {...form.getInputProps('frequency')}
            />

            {form.values.frequency === 'weekly' && (
              <Select
                label="Day of Week"
                data={DAYS_OF_WEEK}
                {...form.getInputProps('day_of_week')}
              />
            )}

            {form.values.frequency === 'monthly' && (
              <NumberInput
                label="Day of Month"
                min={1}
                max={28}
                {...form.getInputProps('day_of_month')}
              />
            )}

            <Group grow>
              <TextInput
                label="Time (24-hour)"
                type="time"
                {...form.getInputProps('time_of_day')}
              />
              <Select label="Timezone" data={TIMEZONES} {...form.getInputProps('timezone')} />
            </Group>

            <div>
              <Text size="sm" fw={500} mb={4}>
                Recipients
              </Text>
              <Group gap="xs" mb="xs">
                <TextInput
                  placeholder="email@example.com"
                  value={recipientInput}
                  onChange={(e) => setRecipientInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addRecipient())}
                  style={{ flex: 1 }}
                />
                <Button variant="light" onClick={addRecipient}>
                  Add
                </Button>
              </Group>
              {form.values.recipients.length > 0 && (
                <Group gap="xs">
                  {form.values.recipients.map((email) => (
                    <Badge
                      key={email}
                      variant="light"
                      rightSection={
                        <ActionIcon
                          size="xs"
                          variant="transparent"
                          onClick={() =>
                            form.setFieldValue(
                              'recipients',
                              form.values.recipients.filter((e) => e !== email)
                            )
                          }
                        >
                          x
                        </ActionIcon>
                      }
                    >
                      {email}
                    </Badge>
                  ))}
                </Group>
              )}
              {form.errors.recipients && (
                <Text size="xs" c="red" mt={4}>
                  {form.errors.recipients}
                </Text>
              )}
            </div>

            <TextInput
              label="Email Subject (optional)"
              placeholder="Leave blank for default"
              {...form.getInputProps('email_subject')}
            />

            <Textarea
              label="Email Introduction (optional)"
              placeholder="Custom message to include in the email"
              {...form.getInputProps('email_intro')}
            />

            <Group>
              <Switch
                label="Include CSV attachment"
                {...form.getInputProps('include_csv', { type: 'checkbox' })}
              />
              <Switch
                label="Include summary stats"
                {...form.getInputProps('include_summary', { type: 'checkbox' })}
              />
            </Group>

            <Group justify="flex-end" mt="md">
              <Button variant="light" onClick={() => setScheduleModalOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={createReport.isPending || createSchedule.isPending}
              >
                Create Schedule
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Container>
  );
}
